import { getFirstQueryParam } from '@jusbrasil-web/shared-utils-url'

import { JUSID_PLATFORM_WEB_AUTH } from '../constants'

export interface AuthPageEventData {
  appId?: string
  artifact?: string
  email?: string
  featureCode?: string
  fromComponent?: string
  visualization?: string
}

export interface SignUpFormPageSeenPayload extends Record<string, string | undefined> {
  email: string
  source_url: string
  app_id: string
  jusid_platform: string
  type: 'default' | 'social'
  social?: string
  useragent?: string
  device_id?: string
  artifact?: string
  feature_code?: string
  from_component?: string
  visualization?: string
}

export interface SignUpFormPageSeenPayloadOptions {
  email: string
  eventData?: AuthPageEventData
  jusidPlatform: string
  type: 'default' | 'social'
  social?: string
  sourceUrl?: string
  userAgent?: string
  deviceId?: string | null
}

type Query = Record<string, string | string[] | undefined>

function toOptional(value: string | null | undefined): string | undefined {
  return value && value.length > 0 ? value : undefined
}

export function getEventDataFromQuery(
  query: Query,
  overrides?: Partial<AuthPageEventData>
): AuthPageEventData {
  const appId = toOptional(getFirstQueryParam(query.app_id))
  const artifact = toOptional(getFirstQueryParam(query.artifact))
  const email = toOptional(getFirstQueryParam(query.email))
  const featureCode = toOptional(getFirstQueryParam(query.feature_code))
  const fromComponent = toOptional(getFirstQueryParam(query.from_component))
  const visualization = toOptional(getFirstQueryParam(query.visualization))
  const data: AuthPageEventData = {
    appId: overrides?.appId ?? appId,
    artifact,
    email: overrides?.email ?? email,
    featureCode: overrides?.featureCode ?? featureCode,
    fromComponent: overrides?.fromComponent ?? fromComponent,
    visualization,
  }
  return Object.fromEntries(
    Object.entries(data).filter(([, v]) => v !== undefined)
  ) as AuthPageEventData
}

export interface BuildUserAccountEventDataOptions {
  type?: string
}

/**
 * Builds snake_case UserAccount event properties for sendMetrics (client-side).
 * app_id and jusid_platform default to JUSID_PLATFORM_WEB_AUTH.
 * Aligns with Bifrost's UserAccount event structure: app_id, artifact, email,
 * feature_code, from_component, jusid_platform, visualization.
 * Omits undefined/empty values.
 */
export function buildUserAccountEventData(
  eventData: AuthPageEventData,
  options?: BuildUserAccountEventDataOptions
): Record<string, string> {
  const { type } = options ?? {}
  const base: Record<string, string> = {
    app_id: eventData.appId ?? JUSID_PLATFORM_WEB_AUTH,
    jusid_platform: JUSID_PLATFORM_WEB_AUTH,
  }
  if (eventData.artifact) base.artifact = eventData.artifact
  if (eventData.email) base.email = eventData.email
  if (eventData.featureCode) base.feature_code = eventData.featureCode
  if (eventData.fromComponent) base.from_component = eventData.fromComponent
  if (eventData.visualization) base.visualization = eventData.visualization
  if (type) base.type = type
  return base
}

export function buildSignUpFormPageSeenPayload({
  email,
  eventData,
  jusidPlatform,
  type,
  social,
  sourceUrl,
  userAgent,
  deviceId,
}: SignUpFormPageSeenPayloadOptions): SignUpFormPageSeenPayload {
  const payload: SignUpFormPageSeenPayload = {
    email,
    source_url: sourceUrl ?? '',
    app_id: eventData?.appId ?? jusidPlatform,
    jusid_platform: jusidPlatform,
    type,
    ...(social && { social }),
  }
  const { artifact, featureCode, fromComponent, visualization } = eventData || {}

  if (userAgent) payload.useragent = userAgent
  if (deviceId) payload.device_id = deviceId
  if (artifact) payload.artifact = artifact
  if (featureCode) payload.feature_code = featureCode
  if (fromComponent) payload.from_component = fromComponent
  if (visualization) payload.visualization = visualization

  return payload
}
